import { Button } from "@/atoms-react/button/Button.react.tsx";
import { Dropdown } from "@/atoms-react/dropdown/index.react";
import { Checkbox } from "@/atoms-react/input-checkbox/index.react";
import InputLabel from "@/atoms-react/input/Input.react";
import Loader from "@/atoms-react/loader/Loader.react";
import { Base, Label } from "@/atoms-react/text/Text.react";
import { useKYCContext } from "@/features/auth/hooks/use-kyc-context";
import { FromURL, type DeclarativeMonthlyIncome, type DeclarativeNif } from "@/features/flow/types";
import { KYCStatusType } from "@/features/kyc/types";
import { convert_naturalPerson_DTO } from "@/features/user/converters";
import { useUser } from "@/features/user/hooks/use-user";
import { useQuiz } from "@/features/user/quiz/hooks/use-quiz";
import { useTaxAddress } from "@/features/user/tax_address/hooks/use-taxAdress";
import type { identityPartners, NaturalPersonDTO, UserType } from "@/features/user/types";
import { getFullName } from "@/features/utils/functions";
import { usei18n } from "@/features/utils/hooks/use-i18n";
import { ModalIbanAccount } from "@/molecules/modal-iban-account/index.react";
import { ModalKYCIdentity } from "@/molecules/modal-kyc-identity/index.react";
import { Themes } from "@/types/theme";
import { PencilIcon } from "@heroicons/react/20/solid";
import type { QueryObserverResult, RefetchOptions } from "@tanstack/react-query";
import _ from "lodash";
import type React from "react";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";
import { TaxAdresseIdentity } from "../identity-np-tax-address/index.react";
import { usePartnerInfo } from "@/features/partner/hooks/use-partner-information";
import { useOrigin } from "@/features/flow/hooks/use-origin";
import { useCompanyContext } from "@/features/company/hooks/use-company-context";

type IdentityDocumentsProps = {
  user: UserType;
  isLoadingUser: boolean;
  refetchUser?: (
    options?: RefetchOptions | undefined
  ) => Promise<QueryObserverResult<UserType, Error>>;
} & React.ComponentPropsWithoutRef<"div">;
const monthlyIncomes: DeclarativeMonthlyIncome[] = [
  {
    trans_key: "less_1500",
    minimum: 0,
    maximum: 1500
  },
  {
    trans_key: "between_1500_and_5000",
    minimum: 1500,
    maximum: 5000
  },
  {
    trans_key: "between_5000_and_10000",
    minimum: 5000,
    maximum: 10000
  },
  {
    trans_key: "more_10000",
    minimum: 10000,
    maximum: 0
  }
];
export const LevelOlkyAccount = ({
  user,
  isLoadingUser = false,
  refetchUser,
  ...props
}: IdentityDocumentsProps) => {
  const { t } = useTranslation(["pass"]);
  const { getLanguage } = usei18n();
  const { kycLevel, auditPending, status, nextStatus } = useKYCContext();
  const originPartner = useOrigin();
  const [profilEdit, setProfilEdit] = useState<
    NaturalPersonDTO & { isDirty: boolean; isDirtyTA?: boolean }
  >({
    ...convert_naturalPerson_DTO(user.naturalPerson),
    isDirtyTA: false,
    isDirty: false
  });
  const [isModalIbanOpen, setIsModalIbanOpen] = useState<boolean>(false);
  const [isModalLevl3Open, setIsModalLevl3Open] = useState<boolean>(false);
  const [modalIdentity, setModalIdentity] = useState<boolean>(false);
  const { useEditProfile } = useUser();
  const { mutateAsync: editProfile, isPending: isEditing } = useEditProfile();
  const { SourcesIncomeQuery } = useQuiz();
  const { data: response, isPending: loadingSI } = SourcesIncomeQuery(getLanguage());
  const sources_income = response?.sourceIncomeCodes;
  const codeOfOtherIncome = sources_income?.find((item) => item.Nom === "Autres revenus")?.id ?? -1;
  const labelClassName = "flex w-40 min-w-40 mb-2 md:mb-0";
  const labelInputDivClassName = "md:flex items-center md:space-x-6 mt-8 w-full ";
  const { leUuid } = useCompanyContext();

  const { updateUserAddressTaxMutation } = useTaxAddress();
  const { mutateAsync: updateTaxAddres, isPending: isPendingUpdate } =
    updateUserAddressTaxMutation();

  const valueDropDown = (() => {
    if (profilEdit.maximumMonthlyIncome === null) profilEdit.maximumMonthlyIncome = 0;
    return monthlyIncomes.find(
      (mi) =>
        mi.minimum?.toString() === profilEdit.minimumMonthlyIncome?.toString() &&
        mi.maximum?.toString() === profilEdit.maximumMonthlyIncome?.toString()
    )?.trans_key;
  })();

  const openKYCModal = () => {
    setModalIdentity(true);
    setIsModalIbanOpen(false);
  };
  const { partnerUuidQuery } = usePartnerInfo();

  const { data: dataPartner } = partnerUuidQuery(
    (sessionStorage.getItem("origin") as string) ?? null
  );

  const allowedToKypay = (() => {
    if (user?.identityPartners) {
      const found = user?.identityPartners?.find(
        (partner: identityPartners) => partner.uuid === dataPartner?.data.partner_uuid
      );

      if (found) {
        if (leUuid) return (kycLevel as number) >= (found?.minimum_olkypass_pro_level as number);
        return (kycLevel as number) >= found?.minimum_olkypass_level;
      }
    }
  })();

  const disablePartnerKypayIbanAccount = (() => {
    if (user?.identityPartners) {
      const found = user?.identityPartners?.find(
        (partner: identityPartners) => partner.uuid === dataPartner?.data.partner_uuid
      );

      if (found) return found.mode === "PRO";
    }
    return false;
  })();
  const handleUpdate = async (
    user: NaturalPersonDTO & { isDirty: boolean; isDirtyTA?: boolean }
  ) => {
    if (profilEdit?.isDirtyTA) {
      let err = false;
      let errDuplicated = false;
      const taxAddressToUpdate = profilEdit.TaxAddress?.filter(
        (ta: DeclarativeNif) => ta.allowUpdate
      );
      if (taxAddressToUpdate && taxAddressToUpdate.length > 0) {
        const map = taxAddressToUpdate.map(async (ta: DeclarativeNif) => {
          if (ta?.country && ta.tin !== null && !ta.apiCheck) {
            err = true;
            return;
          }
          const duplicated = user?.TaxAddress?.filter(
            (t: DeclarativeNif) => t.country === ta?.country
          );
          if (duplicated && duplicated.length > 1) {
            errDuplicated = true;
            return;
          }

          updateTaxAddres({
            taxAddress: {
              apiCheck: ta.apiCheck,
              tin: ta.tin,
              country: ta.country,
              tinExist: ta.tinExist,
              UUID: ta.UUID,
              tinCheck: ta.tinCheck
            }
          }).then(() => {
            setProfilEdit((prevState) => ({
              ...prevState,
              TaxAddress: profilEdit.TaxAddress?.map((t: DeclarativeNif) => {
                if (t.tin === ta.tin && t.country === ta.country && t?.allowUpdate)
                  return { ...t, allowUpdate: false };
                return t;
              })
            }));
          });
        });

        Promise.all(map).then(() => {
          setProfilEdit((prevState) => ({
            ...prevState,
            isDirtyTA: false
          }));
          if (!user.isDirty)
            toast.success(t("olkypass.accountPage.notifications.success.profile.update"));
        });

        if (err) {
          toast.error(t("flow.id_control.error.must_enter_fields"));

          return;
        }
        if (errDuplicated) {
          toast.error(t("flow.id_control.input.nationality_add_error"));
          return;
        }
      }
    }

    if (user.isDirty)
      editProfile(user).then(async () => {
        await refetchUser?.();
        setProfilEdit((prevState) => ({
          ...prevState,
          isDirty: false
        }));
        toast.success(t("olkypass.accountPage.notifications.success.profile.update"));
        if (user?.UUID) {
          const cachedFreshUser = {
            email: user?.AliasEmail?.[0]?.Mail || "",
            name: getFullName(user),
            avatar: ""
          };
          sessionStorage.setItem("user", JSON.stringify(cachedFreshUser));
        }
      });
  };
  const handleCheckboxChange = (id: number, isChecked: boolean) =>
    setProfilEdit((prevState) => {
      const newSourcesIncome = isChecked
        ? [...prevState.sourcesIncome, { identity_uuid: "", source_income_id: id, comment: "" }]
        : prevState.sourcesIncome.filter(
            (income) => Number(income.source_income_id) !== Number(id)
          );

      return {
        ...prevState,
        isDirty: true,
        sourcesIncome: newSourcesIncome
      };
    });
  useEffect(() => {}, [profilEdit]);
  const handleCommentChange = (id: number, otherInputIncomes: string) =>
    setProfilEdit((prevState) => {
      const updatedSourcesIncome = prevState.sourcesIncome.map((income) =>
        income.source_income_id === Number(id)
          ? { ...income, comment: otherInputIncomes, identity_uuid: "" }
          : income
      );
      return {
        ...prevState,
        isDirty: true,
        sourcesIncome: updatedSourcesIncome
      };
    });
  const onInputChange = (person: NaturalPersonDTO) =>
    setProfilEdit((prevState) => ({
      ...prevState,
      TaxAddress: person.TaxAddress,
      isDirty: prevState.isDirty,
      isDirtyTA: true
    }));
  const disabled =
    status !== KYCStatusType.DECLARATIVE ||
    (status === KYCStatusType.DECLARATIVE && nextStatus === KYCStatusType.VERIFIED);
  const disableLvl3 =
    (status === KYCStatusType.VERIFIED && nextStatus === KYCStatusType.AUTHENTICATED) ||
    (kycLevel && kycLevel >= 3)
      ? true
      : false;

  const isSourceIncomeEmpty =
    profilEdit.sourcesIncome.length === 0 || user.naturalPerson.sourcesIncome.length === 0;

  return (
    <div
      className={twMerge("flex flex-col justify-between md:ml-6 md:block md:pt-4 md:pb-6 2xl:ml-0")}
      {..._.omit(props, "className")}
    >
      <ModalIbanAccount
        isOpen={isModalIbanOpen}
        onlyLvl3={false}
        allowedConsoPartner={allowedToKypay}
        onClose={() => {
          sessionStorage.removeItem("DocumentKsyc");
          sessionStorage.removeItem("DocumentQuizKyc");
          setIsModalIbanOpen(false);
        }}
        openKYCModal={openKYCModal}
      />
      <ModalIbanAccount
        isOpen={isModalLevl3Open}
        onlyLvl3={true}
        onClose={() => {
          sessionStorage.removeItem("DocumentKsyc");
          sessionStorage.removeItem("DocumentQuizKyc");
          setIsModalLevl3Open(false);
        }}
        openKYCModal={openKYCModal}
      />
      <div className="flex w-full flex-col p-4">
        <div className="flex flex-col flex-wrap lg:flex-row">
          <Button
            size="md"
            theme={Themes.primary}
            disabled={
              (((kycLevel === 2 && auditPending && nextStatus === KYCStatusType.AUTHENTICATED) ||
                (user?.olkyPassLevel?.level as number) >= 3 ||
                user.naturalPerson.olkypay_account_requested === 1 ||
                isSourceIncomeEmpty ||
                user.naturalPerson.codeShop) as boolean) || kycLevel === 3
            }
            onClick={() => setIsModalLevl3Open(true)}
            iconPosition="before"
            className="mt-4 h-8 w-48 border-none text-xs hover:bg-slate-50 sm:h-10 sm:w-64 sm:text-base lg:mr-5 lg:h-8 lg:w-72 lg:text-sm xl:h-10 xl:text-base xl:text-lg dark:hover:bg-slate-50"
          >
            {t("aliasPaymentsPage.button.level3_modal")}
          </Button>
          <Button
            size="md"
            theme={Themes.primary}
            disabled={
              ((kycLevel === 2 && auditPending && nextStatus === KYCStatusType.AUTHENTICATED) ||
                (originPartner === FromURL.Partner && disablePartnerKypayIbanAccount) ||
                user.naturalPerson.olkypay_account_requested === 1 ||
                isSourceIncomeEmpty ||
                user.naturalPerson.codeShop) as boolean
            }
            onClick={() => setIsModalIbanOpen(true)}
            iconPosition="before"
            className="mt-4 h-8 border-none text-xs hover:bg-slate-50 sm:h-10 sm:w-max sm:text-base lg:h-8 lg:text-sm xl:h-10 xl:text-base xl:text-lg dark:hover:bg-slate-50"
          >
            {(kycLevel === 2 && auditPending && nextStatus === KYCStatusType.AUTHENTICATED) ||
            (kycLevel && kycLevel >= 3)
              ? t("aliasPaymentsPage.button.open_account")
              : t("aliasPaymentsPage.button.iban_account")}
          </Button>
        </div>
      </div>
      <ModalKYCIdentity onClose={() => setModalIdentity(false)} open={modalIdentity} />
      <div className="w-full p-2 md:mb-6 md:p-0 2xl:mb-0">
        <div
          className={twMerge("md:px-6 md:py-4 md:pb-6", props.className)}
          {..._.omit(props, "className")}
        >
          <hr className="mt-6 mb-6"></hr>
          <div className="mb-5 flex justify-between">
            <Base className="hidden text-slate-300 md:block">
              {t("olkypass.mainPage.olkypass.modalOpenOlkyPay.steps.nif.step_title")}
            </Base>
            {isLoadingUser && <Loader />}
            {(profilEdit.isDirty || profilEdit?.isDirtyTA) && !isLoadingUser && (
              <Button
                className="w-max shrink-0"
                loading={isEditing || isPendingUpdate}
                onClick={() => handleUpdate(profilEdit)}
                disabled={(kycLevel && kycLevel >= 3) || (kycLevel === 2 && auditPending)}
                icon={<PencilIcon />}
                theme={Themes.primary}
              >
                {t("olkypass.general.save")}
              </Button>
            )}
          </div>
          <TaxAdresseIdentity
            refetchUser={refetchUser}
            np={user?.naturalPerson}
            disabled={disabled}
            disableLvl3={disableLvl3}
            userForm={profilEdit}
            onInputChange={onInputChange}
          />
          <hr className="mt-6 mb-6" />

          <div className="mb-5 flex justify-between">
            <Base className="hidden text-slate-300 md:block">
              {t("aliasPaymentsPage.title_forms.incomes")}
            </Base>
            {isLoadingUser && <Loader />}
          </div>
          <div className={labelInputDivClassName}>
            <Label className={labelClassName}>
              {t("olkypass.mainPage.olkypass.BronzeForms.net_income")}{" "}
            </Label>
            <Dropdown
              fromQuiz={true}
              disabled={disableLvl3}
              onChange={(item) => {
                const mIncome = monthlyIncomes.find(
                  (mi) =>
                    t(
                      "olkypass.mainPage.olkypass.modalOpenOlkyPay.steps.monthly_incomes.responses." +
                        mi.trans_key
                    ) === (item.value as string)
                );

                if (mIncome)
                  setProfilEdit((prevState) => ({
                    ...prevState,
                    isDirty: true,
                    minimumMonthlyIncome: mIncome.minimum,
                    maximumMonthlyIncome: mIncome.maximum
                  }));
              }}
              required={true}
              initialValue={"Célibataire"}
              className="w-full"
              search={true}
              value={valueDropDown}
              items={monthlyIncomes.map((mi) => {
                return {
                  key: mi.trans_key,
                  value: t(
                    "olkypass.mainPage.olkypass.modalOpenOlkyPay.steps.monthly_incomes.responses." +
                      mi.trans_key
                  )
                };
              })}
            />
          </div>
          <div className={labelInputDivClassName}>
            <Label className={labelClassName}>
              {t("olkypass.mainPage.olkypass.BronzeForms.source_income")}{" "}
            </Label>
            <div className="flex w-full flex-col">
              {!loadingSI ? (
                <div className="my-4 mt-4 grid grid-cols-1 gap-2 md:grid-cols-2">
                  {sources_income &&
                    sources_income.map((c) => (
                      <Checkbox
                        key={c.id}
                        disabled={disableLvl3}
                        value={
                          profilEdit?.sourcesIncome &&
                          profilEdit?.sourcesIncome.some(
                            (income) => income.source_income_id === c.id
                          )
                        }
                        onChange={(value) => handleCheckboxChange(c.id, value)}
                      >
                        <Base className="mb-1 cursor-pointer">
                          {getLanguage() === "fr" ? c.Nom : c.NomEn}
                        </Base>
                      </Checkbox>
                    ))}
                  {profilEdit.sourcesIncome.length === 0 ||
                  user.naturalPerson.sourcesIncome.length === 0 ? (
                    <Base className="mt-3 text-error text-sm">
                      {" "}
                      {t("olkypass.accountPage.profileSection.incompleteDataTitle")}
                    </Base>
                  ) : (
                    <></>
                  )}
                </div>
              ) : (
                <Loader />
              )}
              {profilEdit?.sourcesIncome &&
                profilEdit?.sourcesIncome.some(
                  (income) => Number(income.source_income_id) === codeOfOtherIncome
                ) && (
                  <div className={"mt-4 w-full"}>
                    <InputLabel
                      disabled={disableLvl3}
                      type="text"
                      label={t(
                        "olkypass.mainPage.olkypass.modalOpenOlkyPay.steps.incomes.responses.others_comment"
                      )}
                      value={
                        profilEdit?.sourcesIncome.find(
                          (income) => Number(income.source_income_id) === codeOfOtherIncome
                        )?.comment
                      }
                      onChange={(value: string) => handleCommentChange(codeOfOtherIncome, value)}
                      className="w-full flex-1"
                    />
                  </div>
                )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
